/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IRefineFullProductSearchOutput } from '@msdyn365-commerce/commerce-entities';
import { IActionContext } from '@msdyn365-commerce/core';
import { ProductRefinerValue, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';

import getFullProducts, { FullProductInput, ProductDetailsCriteria } from '../get-full-products';

/**
 * Common refinable products input includes both product detail criteria and refinement criteria.
 */
export interface IRefinableProductsInput {
    refinementCriteria: ProductRefinerValue[];
    productDetailsCriteria?: ProductDetailsCriteria;
}

/**
 * If refinement criteria is set, then gets refined products.
 * @param input Refinable products input.
 * @param ctx Action context.
 * @param getRefinedProducts Callback function to get a promise (e.g. RefineSearchByCategoryAsync, refineSearchByTextAsync)
 * that is executed to get refined products or full set of products.
 */
export async function getRefinedFullProducts(
    input: IRefinableProductsInput,
    ctx: IActionContext,
    getRefinedProducts: () => Promise<ProductSearchResult[]>
): Promise<IRefineFullProductSearchOutput> {

    if (!input || !input.refinementCriteria) {
        ctx.trace('[getFullProductsByRefineSearchCategoryAction] Invalid input.');
        return <IRefineFullProductSearchOutput>{};
    }

    if (input.refinementCriteria.length === 0) {
        ctx.trace('[getFullProductsByRefineSearchCategoryAction] No refiners specified.');
        return <IRefineFullProductSearchOutput>{};
    }
    const { apiSettings } = ctx.requestContext;

    let fullProductInputs: FullProductInput[] = [];

    const searchResults = await getRefinedProducts();
    if (!searchResults) {
        ctx.telemetry.error('[getFullProductsByRefineSearchCategoryAction] no searchResults returned');
    } else {
        fullProductInputs = searchResults.map(
            (product: ProductSearchResult): FullProductInput => {
                return new FullProductInput(
                    product.RecordId,
                    apiSettings,
                    input.productDetailsCriteria || new ProductDetailsCriteria(),
                    ctx.requestContext);
            }
        );
    }

    if (fullProductInputs.length > 0) {
        const productResult = await getFullProducts(fullProductInputs, ctx);
        return {
            productSearchResult: productResult,
            refinementCriteria: input.refinementCriteria
        };
    }
    return {
        productSearchResult: [],
        refinementCriteria: input.refinementCriteria
    };

}
